<script>
import ThemeLoader from '@/components/common/ThemeLoader.vue';

export default defineComponent({
  name: 'BaseBtn',

  components: {
    ThemeLoader,
  },

  inheritAttrs: false,

  props: {
    as: {
      type: String,
      default: undefined,
    },

    /** @type import('vue').PropType<'primary' | 'secondary' | 'ghost' | 'danger' | 'contrast' | 'neutral' | 'ghost-outlined' */
    color: {
      type: String,
      default: 'primary',
    },

    label: {
      type: [String, Number],
      default: undefined,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    loading: {
      type: Boolean,
      default: false,
    },

    loaderSize: {
      type: [String, Number],
      default: '1.75em'
    },

    variant: {
      /** @type import('vue').PropType<'default' | 'two-line' | 'round' | 'icon' | 'dense' | 'plain'> */
      type: String,
      default: 'default',
    },
  },

  computed: {
    computedColor() {
      if (this.variant === 'plain' && this.color === 'primary')
        return 'ghost';

      return this.color;
    },

    isDisabled() {
      return this.loading || this.disabled;
    },

    tagName() {
      if (this.as) {
        return this.as;
      }

      return this.$attrs.href ? 'a' : 'button';
    },

    mergedAttributes() {
      let tagAttributes;

      if (this.tagName === 'a') {
        tagAttributes = {
          'aria-disabled': this.isDisabled ? 'true' : 'false',
          'tabindex': this.isDisabled ? '-1' : this.$attrs.tabindex,
        };
      }
      else if (this.tagName === 'button') {
        tagAttributes = {
          disabled: this.isDisabled,
          type: this.$attrs.type || 'button',
        };
      }

      return { ...this.$attrs, ...tagAttributes };
    },
  },
});
</script>

<template>
  <component
    :is="tagName"
    v-bind="mergedAttributes"
    :class="{
      [$style.container]: true,
      [$style['is-disabled']]: isDisabled,
      [$style.loading]: loading,
      [$style[variant]]: true,
      [$style[computedColor]]: computedColor,
    }"
    v-on="$listeners"
  >
    <i v-if="loading || $scopedSlots.loader" :class="$style.loader">
      <slot name="loader">
        <ThemeLoader :size="loaderSize" style="color: inherit" />
      </slot>
    </i>

    <span :class="$style.content">
      <slot name="prepend-inner" />

      <slot v-if="label || $scopedSlots.default">{{ label }}</slot>

      <slot name="append-inner" />
    </span>
  </component>
</template>

<style lang="css" module>
.container {
  position: relative;

  display: grid;
  grid-template-rows: 1fr;
  grid-auto-flow: column;
  align-items: center;
  justify-items: center;

  font-size: 0.85rem;
  font-weight: 500;
  line-height: 1;

  background-color: var(--bg-color, unset);
  color: var(--text-color, unset);

  border-radius: 12px;
}

a.container {
  text-decoration: none;
}

@media (prefers-reduced-motion: no-preference) {
  .container {
    transition:
      color 150ms ease-in,
      opacity 150ms ease-in,
      background-color 150ms ease-in;
  }
}

.container.is-disabled:not(.loading) {
  cursor: not-allowed;
}

.container.loading {
  cursor: progress;

  grid-template: 1fr / 1fr;
  justify-items: center;
  align-items: center;
}

.content {
  display: inline-flex;
  align-items: center;

  gap: 6px;
}

.loader {
  flex-shrink: 0;
  line-height: 0;
}

/* При loading помещаем все элементы в одну строку, и прячем все элементы, кроме первого (лоадера) */
.container.loading > * {
  grid-row: 1 / 1;
  grid-column: 1 / -1;
}

.container.loading > .content {
  visibility: hidden;
}

/* VARIANTS */

.default {
  min-height: 2.8rem;
  padding: 9px 18px;
}

.two-line {
  font-size: 0.8rem;
  font-weight: 500;
  line-height: 1.2;

  justify-items: start;
  padding: 9px;
}

.two-line > .content {
  align-items: start;
  flex-direction: column;
  row-gap: 3px;
}

.round {
  padding: 9px;

  border-radius: 9999px;
}

.round:not(.ghost) {
  -webkit-appearance: none;

  -webkit-box-shadow: 0px 0px 24px 0px #35394912;
  box-shadow: 0px 0px 24px 0px #35394912;
  border: 1px solid var(--bnw200);
}

.dense {
  padding: 9px;
}

.plain {
  padding: 0;
  background-color: transparent;
}

@media (prefers-reduced-motion: no-preference) {
  .icon > * {
    transition: opacity 150ms ease-in;
  }
}

/* какой-то частный случай, когда кнопка не имеет характеристик, только иконку, но как-то взаимодействие должно проходить */
.icon:hover:enabled > * {
  opacity: 0.75;
}

/* --------COLORS---------- */

.primary {
  --bg-color: var(--primary);
  --text-color: #fff;
}

.primary:hover {
  --bg-color: var(--electric900);
}

.primary:active,
.primary:focus {
  --bg-color: var(--electric1000);
}

.primary:disabled,
.primary.is-disabled {
  --bg-color: var(--electric200);
  --text-color: var(--bnw100);
}

/* --- */

.secondary {
  --bg-color: var(--electric50);
  --text-color: var(--primary);
}

.secondary:hover {
  --bg-color: var(--electric100);
}

.secondary:active,
.secondary:focus {
  --bg-color: var(--electric200);
}

.secondary:disabled,
.secondary.is-disabled {
  --bg-color: var(--bnw100);
  --text-color: var(--bnw500);
}

/* --- */

.secondary.plain:hover {
  --text-color: var(--electric900);
}

.secondary.plain:active,
.secondary.plain:focus {
  --text-color: var(--electric1000);
}

.secondary.plain:disabled,
.secondary.plain.is-disabled {
  --text-color: var(--bnw500);
}

/* --- */
.ghost,
.ghost-outlined {
  --bg-color: rgba(255, 255, 255, 0);
  --text-color: var(--bnw800);
}

.ghost-outlined {
  border: 1px solid var(--bnw300);
}

.ghost:hover,
.ghost-outlined:hover {
  --bg-color: var(--bnw100);
  --text-color: var(--bnw900);
}

.ghost:active,
.ghost:focus,
.ghost-outlined:active
.ghost-outlined:focus {
  --bg-color: var(--bnw200);
  --text-color: var(--bnw900);
}

.ghost:disabled,
.ghost.is-disabled
.ghost-outlined:disabled,
.ghost-outlined.is-disabled {
  --text-color: var(--bnw600);
}

.neutral {
  --bg-color: var(--bnw100);
  --text-color: var(--bnw800);
}

.neutral:hover {
  --bg-color: var(--bnw200);
  --text-color: var(--bnw900);
}

.neutral:active,
.neutral:focus {
  --bg-color: var(--bnw300);
  --text-color: var(--bnw900);
}

.neutral:disabled,
.neutral.is-disabled {
  --text-color: var(--bnw600);
}

.danger {
  --bg-color: var(--coral100);
  --text-color: var(--coral800);
}

.danger:hover {
  --bg-color: var(--coral200);
}

.danger:active,
.danger:focus {
  --bg-color: var(--coral300);
}

.danger:disabled,
.danger.is-disabled {
  --bg-color: var(--coral100);
  --text-color: var(--coral200);
}

/* --- */

.contrast {
  --bg-color: var(--bnw900);
  --text-color: #fff;
}

.contrast:hover {
  --bg-color: var(--bnw800);
}

.contrast:active,
.contrast:focus {
  --bg-color: var(--bnw700);
}

.contrast:disabled,
.contrast.is-disabled {
  --bg-color: var(--bnw600);
  --text-color: var(--bnw300);
}

.plain.contrast {
  --bg-color: none;
  --text-color: #fff;
}

.plain.contrast:hover {
  --text-color: var(--bnw400);
}

.plain.contrast:active,
.plain.contrast:focus {
  --text-color: var(--bnw500);
}

.plain.contrast:disabled,
.plain.contrast.is-disabled {
  --text-color: var(--bnw700);
}
</style>
